import type { FormikProps, FormikValues } from 'formik';
import { useEffect, useRef, useState, type FC } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { useOutletContext } from 'react-router';
import * as Yup from 'yup';

import Auth from '../../../api/auth';
import Provision from '../../../api/provision';
import { FormikForm } from '../../../components/Formik';
import { isValidPassword } from '../../../helpers/validation';
import useApiQuery from '../../../hooks/useApiQuery';

import { useUserData } from '@/redux/selectors';
import type { FormikFormField } from '@/types/formTypes';

interface VirtualCloud {
  id: string;
  name: string;
  juiceGroup: string;
}

interface Product {
  _id: string;
  details: {
    juiceGroup: string;
  };
}

type CloudFieldReturn = FormikFormField;

interface FormData {
  cloudId: string;
  newPasswordCloud: string;
  confirmPasswordCloud: string;
}

interface VirtualCloudResponse {
  cloud: {
    name: string;
  };
}

const CloudField = (): CloudFieldReturn => {
  const intl = useIntl();
  const userData = useUserData();
  const [virtualClouds, setVirtualClouds] =
    useOutletContext<[VirtualCloud[] | undefined, (clouds: VirtualCloud[]) => void]>();
  const [products, setProducts] = useState<Product[]>([]);

  const { makeApiCall: fetchProducts } = useApiQuery(
    Auth.fetchV2Products as unknown as (...args: unknown[]) => Promise<Product[]>
  );
  const { makeApiCall: getVirtualCloudsCall } = useApiQuery(Provision.fetchVirtualClouds);

  useEffect(() => {
    const getProducts = async (): Promise<void> => {
      const response = await fetchProducts();
      const data = (
        response && typeof response === 'object' && 'docs' in response
          ? (response as { docs: Product[] }).docs
          : response
      ) as Product[] | null | undefined;

      if (data) {
        setProducts(data.filter((product: Product) => userData.productIds.includes(product._id)));
      }
    };

    if (virtualClouds === undefined) {
      getProducts();
    }
  }, [fetchProducts, userData.productIds, virtualClouds]);

  useEffect(() => {
    const getVirtualClouds = async (): Promise<void> => {
      const cloudsMap = new Map<string, VirtualCloud>();
      const uniqueJuiceGroups = [...new Set(products.map((p: Product) => p.details.juiceGroup))];
      await Promise.all(
        uniqueJuiceGroups.map(async (juiceGroup: string) => {
          const data = (await getVirtualCloudsCall({ juiceGroups: [juiceGroup] })) as
            VirtualCloudResponse[] | undefined;
          (data ?? []).forEach((vc: VirtualCloudResponse) => {
            if (!cloudsMap.has(vc.cloud.name)) {
              cloudsMap.set(vc.cloud.name, { id: vc.cloud.name, name: vc.cloud.name, juiceGroup });
            }
          });
        })
      );
      if (cloudsMap.size > 0) setVirtualClouds([...cloudsMap.values()]);
    };

    if (products.length) {
      getVirtualClouds();
    }
  }, [getVirtualCloudsCall, products, setVirtualClouds]);

  return {
    fieldType: 'select',
    name: 'cloudId',
    label: intl.formatMessage({ id: 'cloud' }),
    menuItems: virtualClouds?.map((item: VirtualCloud) => ({
      value: item.id,
      label: item.name,
    })),
  } as CloudFieldReturn;
};

const CloudPasswordReset: FC = () => {
  const intl: IntlShape = useIntl();
  const formRef = useRef<FormikProps<FormData>>(null);
  const [virtualClouds] =
    useOutletContext<[VirtualCloud[] | undefined, (clouds: VirtualCloud[]) => void]>();
  const { makeApiCall: updateCloudPassword } = useApiQuery(
    Provision.updateCloudPassword as unknown as (...args: unknown[]) => Promise<unknown>,
    {
      successMessage: 'cloudPasswordUpdate',
    }
  );

  const fields: CloudFieldReturn[] = [
    CloudField(),
    {
      fieldType: 'password',
      name: 'newPasswordCloud',
      label: intl.formatMessage({ id: 'newPassword' }),
    },
    {
      fieldType: 'password',
      name: 'confirmPasswordCloud',
      label: intl.formatMessage({ id: 'confirmNewPasswordField' }),
    },
  ];

  const initialValues = {
    cloudId: '',
    newPasswordCloud: '',
    confirmPasswordCloud: '',
  } as FormikValues;

  const validationSchema = Yup.object().shape({
    cloudId: Yup.string().required(intl.formatMessage({ id: 'formRequiredError' })),
    newPasswordCloud: Yup.string()
      .test(
        'Strong password',
        intl.formatMessage({ id: 'ERR_PASSWORD_INVALID' }),
        (value: string | undefined) => (value ? isValidPassword(value) : true)
      )
      .required(intl.formatMessage({ id: 'formRequiredError' })),
    confirmPasswordCloud: Yup.string()
      .oneOf(
        [Yup.ref('newPasswordCloud'), undefined],
        intl.formatMessage({ id: 'userPasswordMismatch' })
      )
      .required(intl.formatMessage({ id: 'formRequiredError' })),
  });

  const handleSubmit = (formData: FormikValues): void => {
    const { cloudId, newPasswordCloud } = formData;
    const juiceGroup = virtualClouds?.find(vc => vc.id === cloudId)?.juiceGroup;

    updateCloudPassword(juiceGroup, cloudId, newPasswordCloud)
      .then(data => {
        if (data) formRef.current?.resetForm();
      })
      .catch((error: unknown) => {
        console.error('Error updating cloud password:', error);
      });
  };

  return (
    <FormikForm
      formRef={formRef}
      fields={fields}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      submitLabel={intl.formatMessage({ id: 'changePasswordButton' })}
    />
  );
};

export default CloudPasswordReset;
